import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosClient from '../../api/axiosClient';
import { 
  Mail, Save, RefreshCw, Code, LayoutTemplate, 
  Info 
} from 'lucide-react';

const EmailTemplates = () => {
  const queryClient = useQueryClient();
  const [selectedSlug, setSelectedSlug] = useState(null);
  
  // Editor State
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  
  // 1. Fetch Templates
  const { data: templates = [], isLoading } = useQuery({
    queryKey: ['emailTemplates'],
    queryFn: async () => {
      const res = await axiosClient.get('/admin/settings/email-templates');
      
      console.log("RAW API RESPONSE:", res); // 🔍 Check Console (F12)

      // Handle standard Axios response { data: [...] } OR direct interceptor response [...]
      if (Array.isArray(res)) {
          return res;
      }
      if (res && Array.isArray(res.data)) {
          return res.data;
      }
      // Fallback for weird structures
      return []; 
    }
  });

  // 2. Select Template Logic
  useEffect(() => {
    if (templates.length === 0) return;

    setSelectedSlug((currentSlug) => {
      if (currentSlug) return currentSlug;

      const firstTemplate = templates[0];
      setSubject(firstTemplate.subject);
      setBody(firstTemplate.body_html);

      return firstTemplate.slug;
    });
  }, [templates]);

  const handleSelect = (tmpl) => {
    setSelectedSlug(tmpl.slug);
    setSubject(tmpl.subject);
    setBody(tmpl.body_html);
  };

  const activeTemplate = templates.find(t => t.slug === selectedSlug);

  // 3. Save Mutation
  const updateMutation = useMutation({
    mutationFn: (data) => axiosClient.put(`/admin/settings/email-templates/${selectedSlug}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['emailTemplates']);
      alert("Template Saved Successfully!");
    },
    onError: (err) => alert(`Error: ${err.message}`)
  });

  // 4. Reset/Init Mutation (In case table is empty)
  const initMutation = useMutation({
    mutationFn: () => axiosClient.post('/admin/system/init-templates'),
    onSuccess: () => {
      queryClient.invalidateQueries(['emailTemplates']);
      alert("System templates restored!");
    }
  });

  const handleSave = () => {
    updateMutation.mutate({ subject, body_html: body });
  };

  // Helper to insert variable (simple copy to clipboard for now)
  const copyVar = (v) => {
    navigator.clipboard.writeText(v);
    alert(`Copied ${v} to clipboard! Paste it in the body.`);
  };

  if (isLoading) return <div className="p-8 text-slate-500">Loading Templates...</div>;

  return (
    <div className="h-[calc(100vh-100px)] flex flex-col md:flex-row gap-6 animate-fade-in">
      
      {/* LEFT: Sidebar List */}
      <div className="w-full md:w-1/3 bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden flex flex-col">
        <div className="p-4 border-b border-slate-100 bg-slate-50 flex justify-between items-center">
          <h2 className="font-bold text-slate-700 flex items-center">
            <LayoutTemplate className="w-4 h-4 mr-2"/> Templates
          </h2>
          <button onClick={() => initMutation.mutate()} className="text-xs text-blue-600 hover:underline" title="Restore Defaults">
            <RefreshCw className="w-3 h-3" />
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto p-2 space-y-2">
          {templates.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full p-10 text-center space-y-4">
                <div className="w-16 h-16 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center">
                    <LayoutTemplate className="w-8 h-8" />
                </div>
                <div>
                    <h3 className="text-lg font-bold text-slate-800">System Templates Missing</h3>
                    <p className="text-slate-500 text-sm max-w-xs mx-auto mt-1">
                        The database has no email templates yet. Click below to generate the defaults.
                    </p>
                </div>
                <button 
                    onClick={() => initMutation.mutate()} 
                    className="bg-blue-600 text-white px-6 py-2 rounded-lg font-bold shadow-lg hover:bg-blue-700 transition-all flex items-center"
                >
                    <RefreshCw className="w-4 h-4 mr-2" /> Initialize Defaults
                </button>
            </div>
            )}
          
          {templates.map((t) => (
            <button
              key={t.id}
              onClick={() => handleSelect(t)}
              className={`w-full text-left p-3 rounded-lg text-sm transition-all border ${
                selectedSlug === t.slug 
                  ? 'bg-blue-50 border-blue-200 text-blue-800 shadow-sm' 
                  : 'bg-white border-transparent hover:bg-slate-50 text-slate-600'
              }`}
            >
              <div className="font-bold flex items-center">
                <Mail className="w-3 h-3 mr-2 opacity-50"/> {t.slug.replace(/_/g, ' ').toUpperCase()}
              </div>
              <div className="truncate text-xs opacity-70 mt-1">{t.subject}</div>
            </button>
          ))}
        </div>
      </div>

      {/* RIGHT: Editor */}
      <div className="flex-1 bg-white rounded-xl shadow-sm border border-slate-200 flex flex-col">
        {activeTemplate ? (
          <>
            {/* Toolbar */}
            <div className="p-4 border-b border-slate-100 flex justify-between items-center">
              <div>
                <h3 className="font-bold text-lg text-slate-800">Edit Template</h3>
                <p className="text-xs text-slate-400">Slug: <span className="font-mono bg-slate-100 px-1 rounded">{selectedSlug}</span></p>
              </div>
              <button 
                onClick={handleSave} 
                disabled={updateMutation.isLoading}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700 font-bold text-sm shadow-md"
              >
                {updateMutation.isLoading ? <RefreshCw className="w-4 h-4 animate-spin"/> : <Save className="w-4 h-4"/>}
                Save Changes
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              
              {/* Subject Input */}
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Email Subject</label>
                <input 
                  value={subject}
                  onChange={e => setSubject(e.target.value)}
                  className="w-full p-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none font-medium"
                />
              </div>

              {/* Variables Help */}
              {activeTemplate.available_variables && (
                <div className="bg-amber-50 border border-amber-100 p-3 rounded-lg">
                  <div className="text-xs font-bold text-amber-800 mb-2 flex items-center">
                    <Info className="w-3 h-3 mr-1"/> Available Variables (Click to Copy)
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {activeTemplate.available_variables.map(v => (
                      <button 
                        key={v} 
                        onClick={() => copyVar(v)}
                        className="bg-white border border-amber-200 text-amber-700 px-2 py-1 rounded text-xs font-mono hover:bg-amber-100 cursor-pointer"
                      >
                        {v}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Body Editor (Split View) */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 h-96">
                <div className="flex flex-col">
                   <label className="block text-xs font-bold text-slate-500 uppercase mb-1 flex items-center">
                      <Code className="w-3 h-3 mr-1"/> HTML Source
                   </label>
                   <textarea 
                      value={body}
                      onChange={e => setBody(e.target.value)}
                      className="flex-1 w-full p-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none font-mono text-sm leading-relaxed resize-none bg-slate-50 text-slate-700"
                   />
                </div>
                
                <div className="flex flex-col">
                   <label className="block text-xs font-bold text-slate-500 uppercase mb-1 flex items-center">
                      <Eye className="w-3 h-3 mr-1"/> Live Preview
                   </label>
                   <div 
                      className="flex-1 w-full p-4 border border-slate-200 rounded-lg overflow-y-auto prose prose-sm max-w-none bg-white"
                      dangerouslySetInnerHTML={{ __html: body }}
                   />
                </div>
              </div>

            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-slate-400">
            <p>Select a template to edit</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Simple icon wrapper
const Eye = ({className}) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></svg>
);

export default EmailTemplates;